import { useEffect, useMemo } from 'react';
import { CircleMarker, MapContainer, Polygon, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { ZoneEntry } from '@auctionradar/shared';

/**
 * Focused minimap for the Atlas detail panel. Reuses the same `ZONES_BUNDLE`
 * geometry the /map page renders, but shrunk into a panel and auto-fit to the
 * selected zone so you can see *exactly* where it sits.
 *
 * Rendering model:
 *   - zone polygon (emerald fill) when the zone has real `rings` (~83% of L3)
 *   - parent L2 outline (dashed) behind it for spatial context
 *   - a centroid pin (CircleMarker — pure SVG, so no broken-marker-icon issue)
 *     which is the *only* marker when a zone has no real polygon
 *     (parent_inherited / synthetic sources)
 *
 * Tiles: Carto dark — keyless, matches the operator-terminal theme.
 */

// NOTE: host is `basemaps` (plural) — `basemap.cartocdn.com` does not resolve.
const CARTO_LIGHT = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const CARTO_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const ACCENT = '#059669';
const MUTED = '#94a3b8';

/**
 * Sources that are NOT real boundaries — drawing their rings is misleading
 * (e.g. `parent_inherited` clones the L2 parent's shape, so an L3 would paint
 * its parent's outline as if it were itself). For these we show only the
 * centroid pin. Mirrors the SKIPPED_SOURCES set on the /map page.
 */
const SYNTHETIC_SOURCES: ReadonlySet<string> = new Set([
  'macro_synthesized',
  'parent_inherited',
  'voronoi',
  'nominatim_bbox',
]);
const hasRealPolygon = (z: ZoneEntry): boolean =>
  !!z.rings && z.rings.length > 0 && !SYNTHETIC_SOURCES.has(z.polygon_source);

type Bounds = [[number, number], [number, number]];

/** bbox is [minLng, minLat, maxLng, maxLat]; Leaflet wants [[lat,lng],[lat,lng]]. */
function boundsFor(zone: ZoneEntry): Bounds | null {
  if (zone.bbox) {
    const [minLng, minLat, maxLng, maxLat] = zone.bbox;
    return [
      [minLat, minLng],
      [maxLat, maxLng],
    ];
  }
  if (zone.centroid) {
    const [lng, lat] = zone.centroid;
    const d = 0.01; // ~1.1km pad when all we have is a point
    return [
      [lat - d, lng - d],
      [lat + d, lng + d],
    ];
  }
  return null;
}

/** Re-fit the viewport whenever the selected zone changes. */
function FitBounds({ bounds, slug }: { bounds: Bounds; slug: string }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 16, animate: true });
    // slug in deps: same-identity bounds array across renders shouldn't refit,
    // but a new zone selection should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return null;
}

/** Bundle rings are [lng,lat]; Leaflet polygons want [lat,lng]. */
function toLatLng(ring: Array<[number, number]>): [number, number][] {
  return ring.map(([lng, lat]) => [lat, lng]);
}

export function ZoneMiniMap({
  zone,
  parent,
}: {
  zone: ZoneEntry;
  parent: ZoneEntry | null;
}) {
  const bounds = useMemo(() => boundsFor(zone), [zone]);
  const center = useMemo<[number, number]>(() => {
    if (zone.centroid) return [zone.centroid[1], zone.centroid[0]];
    if (bounds)
      return [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
    return [37.99, 23.75]; // Attica fallback
  }, [zone, bounds]);

  if (!bounds) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-term-faint">
        Δεν υπάρχουν γεωγραφικά δεδομένα για αυτή τη ζώνη.
      </div>
    );
  }

  return (
    <MapContainer
      center={center}
      zoom={14}
      zoomControl={false}
      scrollWheelZoom={false}
      doubleClickZoom
      dragging
      className="h-full w-full"
    >
      <TileLayer url={CARTO_LIGHT} attribution={CARTO_ATTR} subdomains="abcd" maxZoom={19} />

      {/* Parent L2 outline — context, drawn first so the zone sits on top. */}
      {parent &&
        hasRealPolygon(parent) &&
        parent.rings?.map((ring, i) => (
          <Polygon
            key={`parent-${i}`}
            positions={toLatLng(ring)}
            pathOptions={{ color: MUTED, weight: 1.2, dashArray: '5 4', fill: false, opacity: 0.7 }}
          />
        ))}

      {/* The zone itself — only when it has a genuine boundary. */}
      {hasRealPolygon(zone) &&
        zone.rings?.map((ring, i) => (
          <Polygon
            key={`zone-${i}`}
            positions={toLatLng(ring)}
            pathOptions={{
              color: ACCENT,
              weight: 2.5,
              // Barely-there fill so the street map reads clearly *underneath*
              // the boundary — the outline locates the zone, the tiles give
              // the context.
              fillColor: ACCENT,
              fillOpacity: 0.06,
              lineJoin: 'round',
            }}
          />
        ))}

      {/* Centroid pin — always shown; the sole marker for polygon-less zones. */}
      {zone.centroid && (
        <CircleMarker
          center={[zone.centroid[1], zone.centroid[0]]}
          radius={5}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: ACCENT, fillOpacity: 1 }}
        />
      )}

      <FitBounds bounds={bounds} slug={zone.slug} />
    </MapContainer>
  );
}
